import React, { useState } from 'react';
import './Homepage.css';
import Header from './Header';
import Footer from './Footer';
import { 
  TwoColumnEqual, 
  OneColumnFull, 
  ThreeColumnEqual,
  TwoColumnSplit,
  FourColumnEqual,
  TwoByTwo
} from './Columntemplates';
import RetroKnob from './Retroknob';

const ExampleUsage = () => {
  const [volume, setVolume] = useState(50);
  const [frequency, setFrequency] = useState(440);
  const [brightness, setBrightness] = useState(75);

  return (
    <>
      <Header />

      <div className="control-panel-wrapper">
        {/* EXAMPLE 1: Two Equal Columns */}
        <TwoColumnEqual
          leftContent={
            <div className="mini-screen">
              <div className="screen-header">~ LEFT PANEL ~</div>
              <div className="screen-body">
                <div className="pixel-text">This is the left column!</div>
                <RetroKnob 
                  label="VOLUME"
                  value={volume}
                  onChange={(val) => setVolume(val)}
                  size="medium"
                />
              </div>
            </div>
          }
          rightContent={
            <div className="mini-screen">
              <div className="screen-header">~ RIGHT PANEL ~</div>
              <div className="screen-body">
                <div className="pixel-text">This is the right column!</div>
                <div className="info-text">Two equal columns are perfect for side-by-side comparisons.</div>
              </div>
            </div>
          }
        />

        {/* EXAMPLE 2: One Big Column */}
        <OneColumnFull
          content={
            <div className="mini-screen wide">
              <div className="screen-header">~ FULL WIDTH PANEL ~</div>
              <div className="screen-body">
                <div className="pixel-text large">BIG CONTENT HERE</div>
                <div className="info-text">
                  Use this for hero sections, main content, or anything that needs maximum width!
                </div>
                <div style={{ display: 'flex', gap: '20px', marginTop: '15px', justifyContent: 'center' }}>
                  <RetroKnob 
                    label="FREQ"
                    minValue={220}
                    maxValue={880}
                    defaultValue={440}
                    onChange={(val) => setFrequency(val)}
                    size="small"
                  />
                  <RetroKnob 
                    label="BRIGHT"
                    value={brightness}
                    onChange={(val) => setBrightness(val)}
                    size="large"
                  />
                  <RetroKnob 
                    label="TUNE"
                    minValue={0}
                    maxValue={10}
                    defaultValue={5}
                    size="small"
                  />
                </div>
              </div>
            </div>
          }
        />

        {/* EXAMPLE 3: Three Equal Columns */}
        <ThreeColumnEqual
          leftContent={
            <div className="mini-screen dark">
              <div className="screen-header">COL 1</div>
              <div className="screen-body">
                <div className="pixel-text">Column 1</div>
              </div>
            </div>
          }
          centerContent={
            <div className="mini-screen dark">
              <div className="screen-header">COL 2</div>
              <div className="screen-body">
                <div className="pixel-text">Column 2</div>
              </div>
            </div>
          }
          rightContent={
            <div className="mini-screen dark">
              <div className="screen-header">COL 3</div>
              <div className="screen-body">
                <div className="pixel-text">Column 3</div>
              </div>
            </div>
          }
        />

        {/* EXAMPLE 4: Two Column Split (1/3 + 2/3) */}
        <TwoColumnSplit
          leftContent={
            <div className="mini-screen">
              <div className="screen-header">SIDEBAR</div>
              <div className="screen-body">
                <div className="pixel-text">Small column (1/3)</div>
                <RetroKnob 
                  label="DIAL"
                  showValue={false}
                  size="small"
                />
              </div>
            </div>
          }
          rightContent={
            <div className="mini-screen">
              <div className="screen-header">MAIN CONTENT</div>
              <div className="screen-body">
                <div className="pixel-text large">Large column (2/3)</div>
                <div className="info-text">Perfect for sidebar + main content layouts!</div>
              </div>
            </div>
          }
        />

        {/* EXAMPLE 5: Four Equal Columns */}
        <FourColumnEqual
          col1={
            <div className="text-panel">
              <div className="label-text">PANEL 1</div>
              <RetroKnob label="A" size="small" showValue={false} />
            </div>
          }
          col2={
            <div className="text-panel">
              <div className="label-text">PANEL 2</div>
              <RetroKnob label="B" size="small" showValue={false} />
            </div>
          }
          col3={
            <div className="text-panel">
              <div className="label-text">PANEL 3</div>
              <RetroKnob label="C" size="small" showValue={false} />
            </div>
          }
          col4={
            <div className="text-panel">
              <div className="label-text">PANEL 4</div>
              <RetroKnob label="D" size="small" showValue={false} />
            </div>
          }
        />

        {/* EXAMPLE 6: Two by Two Grid */}
        <TwoByTwo
          topLeft={
            <div className="mini-screen dark">
              <div className="screen-header">TOP LEFT</div>
              <div className="screen-body">
                <div className="pixel-text">Grid position 1,1</div>
              </div>
            </div>
          }
          topRight={
            <div className="mini-screen dark">
              <div className="screen-header">TOP RIGHT</div>
              <div className="screen-body">
                <div className="pixel-text">Grid position 1,2</div>
              </div>
            </div>
          }
          bottomLeft={
            <div className="mini-screen dark">
              <div className="screen-header">BOTTOM LEFT</div>
              <div className="screen-body">
                <div className="pixel-text">Grid position 2,1</div>
              </div>
            </div>
          }
          bottomRight={
            <div className="mini-screen dark">
              <div className="screen-header">BOTTOM RIGHT</div>
              <div className="screen-body">
                <div className="pixel-text">Grid position 2,2</div>
              </div>
            </div>
          }
        />

        {/* KNOB SHOWCASE */}
        <OneColumnFull
          content={
            <div className="mini-screen wide">
              <div className="screen-header">✿ RETRO KNOB SHOWCASE ✿</div>
              <div className="screen-body">
                <div className="pixel-text large" style={{ marginBottom: '20px' }}>
                  Interactive Dial Controls 🎚️
                </div>
                <div style={{ 
                  display: 'flex', 
                  gap: '30px', 
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  padding: '20px'
                }}>
                  <RetroKnob 
                    label="SMALL"
                    size="small"
                    minValue={0}
                    maxValue={100}
                    defaultValue={25}
                  />
                  <RetroKnob 
                    label="MEDIUM"
                    size="medium"
                    minValue={0}
                    maxValue={100}
                    defaultValue={50}
                  />
                  <RetroKnob 
                    label="LARGE"
                    size="large"
                    minValue={0}
                    maxValue={100}
                    defaultValue={75}
                  />
                  <RetroKnob 
                    label="NO VALUE"
                    size="medium"
                    showValue={false}
                  />
                </div>
                <div className="info-text" style={{ textAlign: 'center', marginTop: '10px' }}>
                  Click and turn like a real radio dial! 🎛️
                </div>
              </div>
            </div>
          }
        />
      </div>

      <Footer />
    </>
  );
};

export default ExampleUsage;